import cx from 'classnames';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  ButtonLink,
  CheckboxField,
  Content,
  FormMessage,
  Header,
  Icon,
  Text,
  TipMessage,
} from '../common/components';
import routes from '../common/constants/routes';
import { useAppDispatch, useDocumentTitle } from '../common/hooks';
import { t } from '../common/i18n';
import i18n from '../common/i18n';
import { setFlashMessage } from '../common/store/uiSlice';
import { formatPrice } from './utils/formatPrice';
import { formatDate } from '../common/utils/Date/Date';
import { createOrder, getOrderOptions } from './services/SubscriptionService';
import { OrderOptions, OrderProduct, OrderProductOption } from './types';

const namespace = 'Subscription';

const orderStatusPath = (orderId: string) => `${routes.subscriptionOrder}order/${orderId}`;

const features = (key: string): string[] => {
  const value = i18n.t(`${namespace}.${key}`, { returnObjects: true });
  return Array.isArray(value) ? (value as string[]) : [];
};

export const OrderForm: React.FC = () => {
  useDocumentTitle(t('title', namespace));

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [options, setOptions] = useState<OrderOptions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [product, setProduct] = useState<OrderProduct>('building-subscription');
  const [quantity, setQuantity] = useState(1);
  const [autoRenew, setAutoRenew] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    getOrderOptions()
      .then((data) => {
        setOptions(data);
        if (!data.products.some((item) => item.product === 'building-subscription') && data.products[0]) {
          setProduct(data.products[0].product);
        }
      })
      .catch(() => setHasLoadError(true))
      .finally(() => setIsLoading(false));
  }, []);

  const selected: OrderProductOption | undefined = useMemo(
    () => options?.products.find((item) => item.product === product),
    [options, product]
  );

  const isSubscription = product === 'building-subscription';
  const count = isSubscription ? 1 : quantity;
  const isRenewal = Boolean(options?.licenseValidTo);

  const handleSubmit = async () => {
    if (!selected) return;
    setIsSubmitting(true);

    try {
      const order = await createOrder({ product, quantity: count, autoRenew: isSubscription ? autoRenew : false });
      navigate(orderStatusPath(order.id));
    } catch (error) {
      // 409 = mezitím vznikla objednávka na stejný produkt (druhý admin, druhá záložka) → ukázat její stav
      if ((error as { status?: number })?.status === 409) {
        const fresh = await getOrderOptions().catch(() => null);
        const openOrderId = fresh?.products.find((item) => item.product === product)?.openOrderId;
        if (openOrderId) {
          navigate(orderStatusPath(openOrderId));
          return;
        }
      }
      dispatch(setFlashMessage({ message: t('submitError', namespace), type: 'error' }));
      setIsSubmitting(false);
    }
  };

  const renderTile = (option: OrderProductOption) => {
    const isFull = option.product === 'building-subscription';
    const isActive = option.product === product;
    const base = isFull ? 'products.subscription' : 'products.poll';
    const title =
      isFull && isRenewal ? t('products.subscription.titleRenewal', namespace) : t(`${base}.title`, namespace);

    return (
      <div
        key={option.product}
        role="radio"
        aria-checked={isActive}
        tabIndex={0}
        className={cx('OrderTile', { 'OrderTile--active': isActive })}
        onClick={() => setProduct(option.product)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setProduct(option.product);
          }
        }}
      >
        <div className="OrderTile__head">
          <div className="OrderTile__icon">
            <Icon name={isFull ? 'shieldCheck' : 'document'} />
          </div>
          <div className="OrderTile__choice">
            {option.openOrderId && !isActive && (
              <span className="OrderTile__pending">{t('products.pending', namespace)}</span>
            )}
            <span className="OrderTile__choiceLabel">
              {t(isActive ? 'products.selected' : 'products.select', namespace)}
            </span>
            <span className={cx('OrderTile__radio', { 'OrderTile__radio--checked': isActive })} aria-hidden="true" />
          </div>
        </div>

        <Text as="div" size="h3" color="Neutral800" className="OrderTile__title">
          {title}
        </Text>
        <Text as="div" size="body-small" color="Neutral700" className="OrderTile__subtitle">
          {t(`${base}.subtitle`, namespace)}
        </Text>

        <ul className="OrderTile__features">
          {features(`${base}.features`).map((feature) => (
            <li key={feature}>
              <Icon name="checkmark" /> <span>{feature}</span>
            </li>
          ))}
        </ul>

        {!isFull && (
          <div className="OrderTile__quantity" onClick={(event) => event.stopPropagation()}>
            <Text as="span" size="body-medium" color="Neutral700">
              {t('products.poll.quantity', namespace)}
            </Text>
            <div className="OrderStepper">
              <button
                type="button"
                className="OrderStepper__button"
                aria-label={t('products.poll.decrease', namespace)}
                disabled={quantity <= 1}
                onClick={() => {
                  setProduct(option.product);
                  setQuantity((value) => Math.max(1, value - 1));
                }}
              >
                −
              </button>
              <span className="OrderStepper__value" aria-live="polite">
                {quantity}
              </span>
              <button
                type="button"
                className="OrderStepper__button"
                aria-label={t('products.poll.increase', namespace)}
                disabled={quantity >= option.maxQuantity}
                onClick={() => {
                  setProduct(option.product);
                  setQuantity((value) => Math.min(option.maxQuantity, value + 1));
                }}
              >
                +
              </button>
            </div>
          </div>
        )}

        <div className="OrderTile__price">
          <Text as="span" className="OrderTile__amount">
            {formatPrice(option.unitPriceWithVat)}
          </Text>
          <Text as="span" size="body-medium" color="Neutral700">
            {' '}
            {t('products.withVat', namespace)} / {t(isFull ? 'products.perYear' : 'products.perPoll', namespace)}
          </Text>
          <Text as="div" size="body-small" color="Neutral600">
            {t('products.withoutVat', namespace, { price: formatPrice(option.unitPriceWithoutVat) })}
          </Text>
        </div>
      </div>
    );
  };

  const billing = options?.billing;

  return (
    <>
      <Header title={t('title', namespace)} backlink={-1} />
      <Content isLoading={isLoading} className="Content Subscription">
        {hasLoadError && <FormMessage type="error" message={t('loadError', namespace)} />}

        {options && selected && (
          <>
            <Text as="h2" size="h3" color="Neutral800" className="Subscription__heading">
              {t('products.heading', namespace)}
            </Text>
            <div className="OrderTiles" role="radiogroup" aria-label={t('products.heading', namespace)}>
              {options.products.map((option, index) => (
                <Fragment key={option.product}>
                  {index > 0 && (
                    <span className="OrderTiles__or" aria-hidden="true">
                      {t('products.or', namespace)}
                    </span>
                  )}
                  {renderTile(option)}
                </Fragment>
              ))}
            </div>

            <section className="OrderSummary">
              <div className="OrderSummary__columns">
                <div className="OrderSummary__column">
                  <Text as="h2" size="body-small" weight="600" color="Neutral600" className="OrderSummary__label">
                    {t('billing.heading', namespace)}
                  </Text>
                  {billing ? (
                    <>
                      <Text as="div" size="body-medium" weight="600" color="Neutral800">
                        {billing.name}
                      </Text>
                      <Text as="div" size="body-medium" color="Neutral700">
                        {[billing.street, `${billing.zip} ${billing.city}`].filter(Boolean).join(', ')}
                      </Text>
                      <Text as="div" size="body-medium" color="Neutral700">
                        {billing.companyId && `${t('billing.companyId', namespace)}: ${billing.companyId}`}
                        {billing.taxId && ` · ${t('billing.taxId', namespace)}: ${billing.taxId}`}
                      </Text>
                      <Text as="div" size="body-medium" color="Neutral700" className="mt-1">
                        {t('billing.email', namespace)} <strong>{billing.email}</strong>{' '}
                        {t('billing.emailNote', namespace)}.
                      </Text>
                    </>
                  ) : (
                    <FormMessage type="error" align="left" message={t('billing.problem', namespace)} />
                  )}
                </div>

                <div className="OrderSummary__column">
                  <Text as="h2" size="body-small" weight="600" color="Neutral600" className="OrderSummary__label">
                    {t('recap.heading', namespace)}
                  </Text>
                  <div className="OrderRecap__row">
                    <Text as="span" size="body-medium" weight="600" color="Neutral800">
                      {isSubscription ? t('recap.subscription', namespace) : t('recap.poll', namespace, { count })}
                    </Text>
                  </div>
                  <div className="OrderRecap__row">
                    <Text as="span" size="body-medium" color="Neutral700">
                      {t('recap.withoutVat', namespace)}
                    </Text>
                    <Text as="span" size="body-medium" color="Neutral700">
                      {formatPrice(selected.unitPriceWithoutVat * count)}
                    </Text>
                  </div>
                  <div className="OrderRecap__row">
                    <Text as="span" size="body-medium" color="Neutral700">
                      {t('recap.vat', namespace, { rate: selected.vatRate / 100 })}
                    </Text>
                    <Text as="span" size="body-medium" color="Neutral700">
                      {formatPrice(selected.vatAmount * count)}
                    </Text>
                  </div>
                  <div className="OrderRecap__row OrderRecap__row--total">
                    <Text as="span" size="body-medium" weight="700" color="Neutral800">
                      {t('recap.total', namespace)}
                    </Text>
                    <Text as="span" className="OrderRecap__total">
                      {formatPrice(selected.unitPriceWithVat * count)}
                    </Text>
                  </div>
                  <Text as="div" size="body-small" color="Neutral600" className="mt-1">
                    {isSubscription &&
                      (options.licenseValidTo
                        ? t('recap.validToRenewal', namespace, {
                            current: formatDate(options.licenseValidTo),
                            date: formatDate(options.newLicenseValidTo),
                          })
                        : t('recap.validToNew', namespace))}{' '}
                    {t('recap.payment', namespace)}
                  </Text>
                </div>
              </div>

              {selected.openOrderId ? (
                <TipMessage variant="info" title={t('openOrder.title', namespace)} className="OrderSummary__footer">
                  <p>{t('openOrder.text', namespace)}</p>
                  <ButtonLink
                    to={orderStatusPath(selected.openOrderId)}
                    title={t('openOrder.action', namespace)}
                    size="small"
                  />
                </TipMessage>
              ) : (
                <div className="OrderSummary__footer OrderSubmit">
                  {isSubscription && (
                    <div className="OrderAutoRenew">
                      <CheckboxField
                        name="autoRenew"
                        label={t('autoRenew.label', namespace)}
                        checked={autoRenew}
                        onChange={(event) => setAutoRenew(event.target.checked)}
                      />
                    </div>
                  )}
                  <Button
                    title={t('submit', namespace)}
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={!billing}
                    disabledDeps={['demo']}
                    className="OrderSubmit__button"
                  />
                </div>
              )}
            </section>
          </>
        )}
      </Content>
    </>
  );
};

export default OrderForm;
